import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';
import {
  LinearProgress,
  Tooltip,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Box,
  IconButton,
  Button as MuiButton,
} from '@mui/material';
import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  OtherHouses as HubIcon,
  Search as SearchIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

import PndRestApi from 'src/utils/pnd/PndRestApi';
import ConfirmationDialog from 'src/shared/components/ConfirmationDialog';
import { showNotification, successNofication } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';

const MerchantHubAssignModal = ({
  modalShow,
  setModalShow,
  reactTableData,
  setRedirect,
}) => {
  const [loading, setLoading] = useState(false);
  const [merchantsList, setMerchantsList] = useState([]);
  const [activeHubList, setActiveHubList] = useState([]);

  // Hub Selection State
  const [primaryHubId, setPrimaryHubId] = useState('');
  const [secondaryHubId, setSecondaryHubId] = useState('');
  const [tertiaryHubId, setTertiaryHubId] = useState('');

  // Selected Merchant IDs for Assignment
  const [selectedMerchantIds, setSelectedMerchantIds] = useState([]);

  // Filter & Search State
  const [searchText, setSearchText] = useState('');
  const [filterTab, setFilterTab] = useState('ALL'); // 'ALL' | 'UNASSIGNED' | 'ASSIGNED'

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Unassign Dialog State
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [unassignData, setUnassignData] = useState(null);
  const [unassignHubType, setUnassignHubType] = useState(1); // 1 = Primary, 2 = Sec, 3 = Ter

  // Fetch all active hubs
  const fetchActiveHubs = async () => {
    try {
      const response = await PndRestApi.GetAllActiveHub();
      if (response && response.status === 200 && response.data?.status === 200) {
        const hubs = (response.data.data || []).map(item => ({
          label: item.hubName,
          value: item.hubId,
        }));
        setActiveHubList(hubs);
      }
    } catch (error) {
      console.error('Error fetching active hubs:', error);
    }
  };

  // Fetch all merchant mapping details
  const fetchMappingDetails = async () => {
    try {
      setLoading(true);
      const response = await PndRestApi.GetAllMerchantHubMappingDetails();
      if (response && response.status === 200 && response.data?.status === 200) {
        setMerchantsList(response.data.data || []);
      } else {
        setMerchantsList(reactTableData || []);
      }
    } catch (error) {
      console.error('Error fetching merchant hub mappings:', error);
      setMerchantsList(reactTableData || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (modalShow) {
      fetchActiveHubs();
      fetchMappingDetails();
      setSelectedMerchantIds([]);
      setPrimaryHubId('');
      setSecondaryHubId('');
      setTertiaryHubId('');
      setSearchText('');
      setPage(0);
    }
  }, [modalShow]);

  const toggle = () => {
    setModalShow(false);
  };

  // Filter secondary and tertiary hub lists based on selections
  const secondaryHubOptions = useMemo(() => {
    if (!primaryHubId) return activeHubList;
    return activeHubList.filter(h => String(h.value) !== String(primaryHubId));
  }, [activeHubList, primaryHubId]);

  const tertiaryHubOptions = useMemo(() => {
    return activeHubList.filter(
      h => String(h.value) !== String(primaryHubId) && String(h.value) !== String(secondaryHubId)
    );
  }, [activeHubList, primaryHubId, secondaryHubId]);

  const selectedPrimaryHubName = useMemo(() => {
    const hub = activeHubList.find(h => String(h.value) === String(primaryHubId));
    return hub ? hub.label : '';
  }, [activeHubList, primaryHubId]);

  // Filter merchants based on search and status tab
  const filteredMerchants = useMemo(() => {
    const q = searchText.toLowerCase().trim();
    return merchantsList.filter(m => {
      const isAssigned = Boolean(m.hubId || m.secHubId || m.terHubId || m.hubName);

      // Status Tab filter
      if (filterTab === 'UNASSIGNED' && isAssigned) return false;
      if (filterTab === 'ASSIGNED' && !isAssigned) return false;

      // Search text filter
      if (!q) return true;
      return (
        m.merchantName?.toLowerCase().includes(q) ||
        m.merchantCode?.toLowerCase().includes(q) ||
        m.merchantBranch?.toLowerCase().includes(q) ||
        m.hubName?.toLowerCase().includes(q) ||
        m.secHubName?.toLowerCase().includes(q) ||
        m.terHubName?.toLowerCase().includes(q)
      );
    });
  }, [merchantsList, searchText, filterTab]);

  // Counts for tabs
  const tabCounts = useMemo(() => {
    const all = merchantsList.length;
    const assigned = merchantsList.filter(m => Boolean(m.hubId || m.secHubId || m.terHubId || m.hubName)).length;
    const unassigned = all - assigned;
    return { all, assigned, unassigned };
  }, [merchantsList]);

  // Visible rows for pagination
  const visibleRows = useMemo(() => {
    return filteredMerchants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredMerchants, page, rowsPerPage]);

  // Assignable merchants are those without a primary hub
  const assignableMerchants = useMemo(() => {
    return filteredMerchants.filter(m => m.hubId === null || m.hubId === undefined);
  }, [filteredMerchants]);

  // Checkbox handling
  const handleCheckboxClick = (e, merchantId) => {
    e.stopPropagation();
    setSelectedMerchantIds(prev => {
      if (prev.includes(merchantId)) {
        return prev.filter(id => id !== merchantId);
      }
      return [...prev, merchantId];
    });
  };

  const handleSelectAllClick = (e) => {
    if (e.target.checked) {
      const assignableIds = assignableMerchants.map(m => m.merchantId);
      setSelectedMerchantIds(assignableIds);
    } else {
      setSelectedMerchantIds([]);
    }
  };

  const isAllSelected =
    assignableMerchants.length > 0 &&
    assignableMerchants.every(m => selectedMerchantIds.includes(m.merchantId));
  const isIndeterminate =
    selectedMerchantIds.length > 0 &&
    !isAllSelected &&
    assignableMerchants.some(m => selectedMerchantIds.includes(m.merchantId));

  // --- Assign Submission ---
  const handleSubmitAssign = async () => {
    if (!primaryHubId) {
      showNotification('theme-light', 'ltr', 'Warning', 'Please select a Primary Hub first.');
      return;
    }
    if (selectedMerchantIds.length === 0) {
      showNotification('theme-light', 'ltr', 'Warning', 'Please select at least one merchant to assign.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        hubId: Number(primaryHubId),
        merchantId: selectedMerchantIds,
        secondaryHubId: secondaryHubId ? Number(secondaryHubId) : null,
        tertiaryHubId: tertiaryHubId ? Number(tertiaryHubId) : null,
      };

      const response = await PndRestApi.AssignMerchantFromHub(payload);
      if (response && response.status === 200 && response.data?.status === 200) {
        successNofication(
          'theme-light',
          'ltr',
          `SuccessCode: ${response.data.status}`,
          'Message: Merchant(s) Assigned to Hub Successfully'
        );
        setSelectedMerchantIds([]);
        await fetchMappingDetails();
        if (setRedirect) setRedirect(true);
      } else {
        showNotification(
          'theme-light',
          'ltr',
          `SuccessCode: ${response?.data?.status || 500}`,
          `Message: ${response?.data?.message || 'Operation failed'}`
        );
      }
    } catch (error) {
      console.error('Error in assigning merchant to hub:', error);
      showNotification('theme-light', 'ltr', 'Error', ERROR_MESSAGES.tryCatchError);
    } finally {
      setLoading(false);
    }
  };

  // --- Unassign Handling ---
  const handleOpenUnassign = (merchant, hubType) => {
    setUnassignData(merchant);
    setUnassignHubType(hubType);
    setShowConfirmation(true);
  };

  const handleConfirmUnassign = async () => {
    if (!unassignData) return;

    try {
      setLoading(true);
      let targetHubId = null;
      if (unassignHubType === 1) targetHubId = unassignData.hubId;
      if (unassignHubType === 2) targetHubId = unassignData.secHubId;
      if (unassignHubType === 3) targetHubId = unassignData.terHubId;

      const payload = {
        hubId: targetHubId,
        merchantId: unassignData.merchantId,
      };

      const response = await PndRestApi.UnAssignMerchantFromHub(payload);
      if (response && response.status === 200 && response.data?.status === 200) {
        successNofication(
          'theme-light',
          'ltr',
          'Message: Merchant Unassigned from Hub Successfully'
        );
        setShowConfirmation(false);
        await fetchMappingDetails();
        if (setRedirect) setRedirect(true);
      } else {
        showNotification(
          'theme-light',
          'ltr',
          'Error',
          'Operation Failed. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error unassigning merchant:', error);
      showNotification('theme-light', 'ltr', 'Error', ERROR_MESSAGES.tryCatchError);
    } finally {
      setShowConfirmation(false);
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={modalShow}
      toggle={toggle}
      size="xl"
      centered
      className="theme-light"
      contentClassName="border-0 shadow-lg overflow-hidden"
    >
      {/* Modern Modal Header */}
      <ModalHeader toggle={toggle} className="border-bottom bg-white py-3 px-4">
        <div className="d-flex align-items-center">
          <div
            style={{
              backgroundColor: '#e0f2fe',
              color: '#0284c7',
              borderRadius: '10px',
              padding: '8px 12px',
              marginRight: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AssignmentTurnedInIcon sx={{ fontSize: 24 }} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: '#0f172a' }}>
              Assign / Unassign Merchants to Hub
            </h5>
            <small className="text-muted" style={{ fontSize: '13px' }}>
              Configure primary, secondary, and tertiary hubs for merchants
            </small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-0 position-relative">
        {loading && (
          <LinearProgress
            color="primary"
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}
          />
        )}

        <Row className="g-0" style={{ minHeight: '600px' }}>
          {/* ── Left Sidebar (Configuration & Actions) ── */}
          <Col md={4} lg={3} className="bg-light border-end p-4 d-flex flex-column">
            {/* Step 1: Destination Hubs */}
            <div className="mb-4">
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#64748b',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                1. Select Destination Hubs
              </Typography>

              {/* Primary Hub Dropdown */}
              <div className="mb-3">
                <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  PRIMARY HUB <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select form-select-sm"
                  value={primaryHubId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPrimaryHubId(val);
                    if (secondaryHubId === val) setSecondaryHubId('');
                    if (tertiaryHubId === val) setTertiaryHubId('');
                  }}
                  style={{
                    borderRadius: '8px',
                    height: '38px',
                    fontSize: '0.85rem',
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    color: '#1e293b',
                  }}
                >
                  <option value="">Select Primary Hub</option>
                  {activeHubList.map((hub) => (
                    <option key={hub.value} value={hub.value}>
                      {hub.label}
                    </option>
                  ))}
                </select>
                {selectedPrimaryHubName && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      borderRadius: '6px',
                      bgcolor: '#e0f2fe',
                      color: '#0369a1',
                      fontSize: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    <HubIcon sx={{ fontSize: 15 }} />
                    <span>Target: {selectedPrimaryHubName}</span>
                  </Box>
                )}
              </div>

              {/* Secondary Hub Dropdown */}
              <div className="mb-3">
                <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  SECONDARY HUB <span className="text-muted fw-normal">(OPTIONAL)</span>
                </label>
                <select
                  className="form-select form-select-sm"
                  value={secondaryHubId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSecondaryHubId(val);
                    if (tertiaryHubId === val) setTertiaryHubId('');
                  }}
                  disabled={!primaryHubId}
                  style={{
                    borderRadius: '8px',
                    height: '38px',
                    fontSize: '0.85rem',
                    backgroundColor: primaryHubId ? '#ffffff' : '#f8fafc',
                    border: '1px solid #cbd5e1',
                    color: '#1e293b',
                  }}
                >
                  <option value="">None (Optional)</option>
                  {secondaryHubOptions.map((hub) => (
                    <option key={hub.value} value={hub.value}>
                      {hub.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tertiary Hub Dropdown */}
              <div className="mb-3">
                <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  TERTIARY HUB <span className="text-muted fw-normal">(OPTIONAL)</span>
                </label>
                <select
                  className="form-select form-select-sm"
                  value={tertiaryHubId}
                  onChange={(e) => setTertiaryHubId(e.target.value)}
                  disabled={!secondaryHubId}
                  style={{
                    borderRadius: '8px',
                    height: '38px',
                    fontSize: '0.85rem',
                    backgroundColor: secondaryHubId ? '#ffffff' : '#f8fafc',
                    border: '1px solid #cbd5e1',
                    color: '#1e293b',
                  }}
                >
                  <option value="">None (Optional)</option>
                  {tertiaryHubOptions.map((hub) => (
                    <option key={hub.value} value={hub.value}>
                      {hub.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Step 2: Selection Summary Card */}
            <div className="mb-auto">
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#64748b',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  mb: 1.5,
                }}
              >
                2. Selection Summary
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  bgcolor: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ color: '#0ea5e9', fontWeight: 800, fontSize: '2.5rem' }}
                >
                  {selectedMerchantIds.length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                  Merchants Selected
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
                  {primaryHubId
                    ? 'Ready to assign to selected hub'
                    : 'Select a primary hub to assign'}
                </Typography>
              </Paper>
            </div>

            {/* Bottom Confirm / Cancel Actions */}
            <div className="mt-4 pt-3 border-top">
              <MuiButton
                fullWidth
                variant="contained"
                onClick={handleSubmitAssign}
                disabled={!primaryHubId || selectedMerchantIds.length === 0 || loading}
                sx={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)',
                  color: 'white',
                  py: 1.2,
                  fontWeight: 700,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  boxShadow: 'none',
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8',
                  },
                }}
              >
                Confirm Assignment ({selectedMerchantIds.length})
              </MuiButton>
              <MuiButton
                fullWidth
                variant="text"
                onClick={toggle}
                sx={{
                  mt: 1,
                  color: '#64748b',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Cancel
              </MuiButton>
            </div>
          </Col>

          {/* ── Right Content (Merchants List with Modern Table) ── */}
          <Col md={8} lg={9} className="bg-white p-4 d-flex flex-column">
            {/* Filter Chips & Search Bar */}
            <Box
              sx={{
                mb: 2.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {/* Filter Tabs */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`All (${tabCounts.all})`}
                  clickable
                  color={filterTab === 'ALL' ? 'primary' : 'default'}
                  variant={filterTab === 'ALL' ? 'filled' : 'outlined'}
                  onClick={() => {
                    setFilterTab('ALL');
                    setPage(0);
                  }}
                  sx={{ fontWeight: 600, fontSize: '12px' }}
                />
                <Chip
                  label={`Unassigned (${tabCounts.unassigned})`}
                  clickable
                  color={filterTab === 'UNASSIGNED' ? 'primary' : 'default'}
                  variant={filterTab === 'UNASSIGNED' ? 'filled' : 'outlined'}
                  onClick={() => {
                    setFilterTab('UNASSIGNED');
                    setPage(0);
                  }}
                  sx={{ fontWeight: 600, fontSize: '12px' }}
                />
                <Chip
                  label={`Assigned (${tabCounts.assigned})`}
                  clickable
                  color={filterTab === 'ASSIGNED' ? 'primary' : 'default'}
                  variant={filterTab === 'ASSIGNED' ? 'filled' : 'outlined'}
                  onClick={() => {
                    setFilterTab('ASSIGNED');
                    setPage(0);
                  }}
                  sx={{ fontWeight: 600, fontSize: '12px' }}
                />
              </Box>

              {/* Search Field */}
              <TextField
                size="small"
                placeholder="Search merchant, code, branch..."
                variant="outlined"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '280px',
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
            </Box>

            {/* Table Container */}
            <Paper
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <TableContainer sx={{ flexGrow: 1, maxHeight: 460 }}>
                <Table stickyHeader size="small" aria-label="merchants table">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell padding="checkbox" align="center" sx={{ width: 48 }}>
                        <Checkbox
                          size="small"
                          color="primary"
                          indeterminate={isIndeterminate}
                          checked={isAllSelected}
                          onChange={handleSelectAllClick}
                          disabled={assignableMerchants.length === 0}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '12px', width: 40 }}>
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '12px' }}>
                        Merchant Details
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '12px' }}>
                        Branch
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '12px' }}>
                        Primary Hub
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '12px' }}>
                        Secondary Hub
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '12px' }}>
                        Tertiary Hub
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <StorefrontIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No merchants found matching current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleRows.map((row, index) => {
                        const isAssignable = row.hubId === null || row.hubId === undefined;
                        const isSelected = selectedMerchantIds.includes(row.merchantId);

                        return (
                          <TableRow
                            key={row.merchantId}
                            hover
                            onClick={(e) => {
                              if (isAssignable) handleCheckboxClick(e, row.merchantId);
                            }}
                            sx={{
                              cursor: isAssignable ? 'pointer' : 'default',
                              bgcolor: isSelected ? '#f0f9ff' : 'transparent',
                            }}
                          >
                            {/* Checkbox Column */}
                            <TableCell padding="checkbox" align="center">
                              {isAssignable ? (
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  checked={isSelected}
                                  onChange={(e) => handleCheckboxClick(e, row.merchantId)}
                                />
                              ) : (
                                <Tooltip title="Already assigned to Primary Hub">
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-block',
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: '#10b981',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>

                            {/* Serial Number */}
                            <TableCell sx={{ color: '#64748b', fontSize: '12px' }}>
                              {page * rowsPerPage + index + 1}
                            </TableCell>

                            {/* Merchant Name & Code */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                  {row.merchantName}
                                </Typography>
                                {row.merchantCode && (
                                  <Chip
                                    label={row.merchantCode}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '10px', color: '#0284c7', borderColor: '#bae6fd' }}
                                  />
                                )}
                              </Box>
                            </TableCell>

                            {/* Branch */}
                            <TableCell sx={{ color: '#64748b', fontSize: '12px' }}>
                              {row.merchantBranch || '—'}
                            </TableCell>

                            {/* Primary Hub */}
                            <TableCell>
                              {row.hubName ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip
                                    icon={<HubIcon sx={{ fontSize: '13px !important', color: '#059669' }} />}
                                    label={row.hubName}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '11px',
                                      bgcolor: '#ecfdf5',
                                      color: '#065f46',
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Tooltip title="Unassign from Primary Hub">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenUnassign(row, 1);
                                      }}
                                      sx={{ p: 0.5 }}
                                    >
                                      <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Chip
                                  label="Unassigned"
                                  size="small"
                                  sx={{ height: 20, fontSize: '10px', bgcolor: '#f1f5f9', color: '#94a3b8' }}
                                />
                              )}
                            </TableCell>

                            {/* Secondary Hub */}
                            <TableCell>
                              {row.secHubName ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip
                                    label={row.secHubName}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '11px',
                                      bgcolor: '#f8fafc',
                                      border: '1px solid #e2e8f0',
                                      color: '#475569',
                                    }}
                                  />
                                  <Tooltip title="Unassign from Secondary Hub">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenUnassign(row, 2);
                                      }}
                                      sx={{ p: 0.5 }}
                                    >
                                      <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            {/* Tertiary Hub */}
                            <TableCell>
                              {row.terHubName ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip
                                    label={row.terHubName}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '11px',
                                      bgcolor: '#f8fafc',
                                      border: '1px solid #e2e8f0',
                                      color: '#475569',
                                    }}
                                  />
                                  <Tooltip title="Unassign from Tertiary Hub">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenUnassign(row, 3);
                                      }}
                                      sx={{ p: 0.5 }}
                                    >
                                      <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Table Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredMerchants.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}
              />
            </Paper>
          </Col>
        </Row>
      </ModalBody>

      {/* Confirmation Dialog for Unassign */}
      {showConfirmation && (
        <ConfirmationDialog
          open={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={handleConfirmUnassign}
          title="Unassign Hub"
          message={`Are you sure you want to unassign ${unassignData?.merchantName || 'this merchant'} from ${
            unassignHubType === 1
              ? unassignData?.hubName
              : unassignHubType === 2
              ? unassignData?.secHubName
              : unassignData?.terHubName
          }?`}
        />
      )}
    </Modal>
  );
};

MerchantHubAssignModal.propTypes = {
  modalShow: PropTypes.bool.isRequired,
  setModalShow: PropTypes.func.isRequired,
  reactTableData: PropTypes.array,
  setRedirect: PropTypes.func,
};

MerchantHubAssignModal.defaultProps = {
  reactTableData: [],
  setRedirect: () => {},
};

export default MerchantHubAssignModal;
